import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useStaffRole } from '../hooks/useStaffRole';
// 🔑 ONE DEFINITION, IN src/constants/staffNav.js. It lived here, and the
// router and the top navbar could not read it without importing a component —
// so the router read nothing and the top navbar filtered by nothing. Moved so
// all three derive from it.
import { SIDEBAR_CONFIGS } from '../constants/staffNav';
import { shellBadgeFeeds } from '../constants/staffNav';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const SIDEBAR_WIDTH = 220;
const COMING_SOON = '/admin/coming-soon';

// ⚠️ RE-EXPORTED, NOT REDEFINED. AdminNavbar imported SIDEBAR_CONFIGS from
// here; the constant now lives in constants/staffNav.js and this keeps the old
// import path resolving rather than making a module move into a rename.
export { SIDEBAR_WIDTH, SIDEBAR_CONFIGS };

export default function AdminSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const [badges, setBadges] = useState({ messages: 0, partnerMessages: 0, alerts: 0, borrowerAlerts: 0, partnerAlerts: 0, marketAlerts: 0, marketMessages: 0, showingRequests: 0, offerRequests: 0, tickets: 0, partnerTasks: 0 });
  // Collapsible sub-menus (e.g. "Active Modules"). Keyed by parent label;
  // value true/false overrides the default (open when a child route is active).
  const [openMenus, setOpenMenus] = useState({});

  // Admin profile (avatar + dropdown). Mirrors AdminNavbar's read-from-
  // localStorage + listen-for-doorly:profileUpdated pattern so both stay
  // in sync when the user edits their profile photo / name. The
  // EditProfile + Logout-confirm modals continue to live in AdminNavbar
  // (the mobile drawer also triggers them); we open them here via
  // CustomEvent so the sidebar avatar drives the same flows.
  const [adminUser, setAdminUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    const handler = () => {
      try { setAdminUser(JSON.parse(localStorage.getItem('doorly_admin_user') || '{}')); } catch {}
    };
    window.addEventListener('doorly:profileUpdated', handler);
    return () => window.removeEventListener('doorly:profileUpdated', handler);
  }, []);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const accountRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setShowAccountDropdown(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  const fireEvent = (name) => window.dispatchEvent(new CustomEvent(name));
  const profilePhotoUrl = adminUser.profile_photo
    ? (adminUser.profile_photo.startsWith('data:') || adminUser.profile_photo.startsWith('http')
        ? adminUser.profile_photo
        : null) // 🚨 a legacy server-path photo pointed at the closed public file mount (2026-10-28)
    : null;

  // 🔑 READ BEFORE THE POLLS, because the polls depend on it: a badge feed for an
  // area this role's nav does not render is not CALLED (constants/staffNav
  // shellBadgeFeeds). For Partner Success that was borrower alerts, borrower
  // request counts and market alerts — three refused calls on every page.
  const { role } = useStaffRole();
  const feeds = shellBadgeFeeds(role);
  // ⚠️ A ZERO FOR A FEED NOT POLLED — the shape each .catch() below already
  // falls back to, so nothing downstream can tell "not polled" from "none".
  const skip = (data) => Promise.resolve({ data });

  useEffect(() => {
    const token = localStorage.getItem('doorly_admin_token');
    if (!token) return;
    const fetchBadges = async () => {
      try {
        const [masterRes, borrowerRes, partnerRes, requestCountsRes, ticketRes, partnerTasksRes] = await Promise.all([
          // ── 🚨 THE ONE CALL WITHOUT A CATCH TOOK OUT EVERY BADGE ──────────
          //
          // Promise.all rejects on the first rejection and the outer `catch {}`
          // swallows it, so for any role this endpoint refuses — partner_success
          // — setBadges never ran AT ALL. Not just the alerts count: partner
          // messages, partner alerts and the TICKET badge were all permanently
          // zero, and all three are that role's actual work. The four siblings
          // already had .catch(); this one did not, and the asymmetry was the
          // whole bug.
          axios.get(`${API}/api/admin/alerts/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
            .catch(() => ({ data: {} })),
          feeds.borrowerAlerts
            ? axios.get(`${API}/api/admin/alerts?category=borrower&status=unread&limit=0`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { unread_count: 0 } }))
            : skip({ unread_count: 0 }),
          feeds.partnerAlerts
            ? axios.get(`${API}/api/admin/alerts?category=partner&status=unread&limit=0`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { unread_count: 0 } }))
            : skip({ unread_count: 0 }),
          feeds.requestCounts
            ? axios.get(`${API}/api/admin/borrowers/request-counts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { showing_requests: 0, offer_requests: 0 } }))
            : skip({ showing_requests: 0, offer_requests: 0 }),
          axios.get(`${API}/api/admin/ticket-badge-count`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { count: 0 } })),
          // 🔑 THE PARTNER TASKS COUNT, from the same queue the page renders —
          // see backend/utils/partnerTaskQueue.partnerVerificationCount for why
          // it is not a COUNT(*). .catch() like its four siblings: the one call
          // here without one took out every badge for partner_success once.
          axios.get(`${API}/api/admin/partner-tasks/count`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { count: 0 } })),
        ]);
        setBadges((prev) => ({
          ...prev,
          messages: masterRes.data.messagesCount || 0,
          partnerMessages: masterRes.data.partnerMessagesCount || 0,
          alerts: masterRes.data.alertsCount || 0,
          borrowerAlerts: borrowerRes.data.unread_count || 0,
          partnerAlerts: partnerRes.data.unread_count || 0,
          showingRequests: requestCountsRes.data.showing_requests || 0,
          offerRequests: requestCountsRes.data.offer_requests || 0,
          tickets: ticketRes.data.count || 0,
          partnerTasks: partnerTasksRes.data.count || 0,
        }));
      } catch {}
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const token = localStorage.getItem('doorly_admin_token');
    if (!token) return;
    // 🔑 NO MARKET SECTION, NO MARKET POLL — see shellBadgeFeeds above.
    if (!feeds.marketAlerts && !feeds.marketMessages) return;
    const fetchMarketBadges = async () => {
      try {
        const [alertsRes, messagesRes] = await Promise.all([
          feeds.marketAlerts
            ? axios.get(`${API}/api/admin/market/alerts/unread-count`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { count: 0 } }))
            : skip({ count: 0 }),
          feeds.marketMessages
            ? axios.get(`${API}/api/market/admin/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { count: 0 } }))
            : skip({ count: 0 }),
        ]);
        setBadges((prev) => ({
          ...prev,
          marketAlerts:   alertsRes.data.count || 0,
          marketMessages: messagesRes.data.count || 0,
        }));
      } catch {}
    };
    fetchMarketBadges();
    const interval = setInterval(fetchMarketBadges, 60000);
    return () => clearInterval(interval);
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Skip configs the active role can't access AT ALL. Within an
  // allowed config, individual items are still filtered below by
  // their own `hideForRoles` field.
  const config = SIDEBAR_CONFIGS.find(
    (c) => c.match(pathname) && !(c.hideForRoles || []).includes(role)
  );
  if (!config) return null;
  const visibleItems = config.items
    .filter((item) => !(item.hideForRoles || []).includes(role))
    .map((item) => {
      if (!item.children) return item;
      // Filter sub-items by role too; drop the parent below if none remain.
      return { ...item, children: item.children.filter((c) => !(c.hideForRoles || []).includes(role)) };
    })
    .filter((item) => !item.children || item.children.length > 0);

  const fullPath = pathname + location.search;
  const isActive = (item) => {
    if (item.path === COMING_SOON) return false;
    // Match query-string paths (e.g. /admin/schedule?view=all)
    if (item.path.includes('?') && item.path === fullPath) return true;
    // Default: /admin/schedule with no query matches ?view=all
    if (item.path.includes('?view=all') && pathname === item.path.split('?')[0] && !location.search) return true;
    if (item.path === pathname && !item.path.includes('?')) return true;
    if (!item.exact && item.path !== '/admin/dashboard' && pathname.startsWith(item.path + '/')) return true;
    return false;
  };

  return (
    <>
      <div
        className="admin-sidebar"
        style={{
          position: 'fixed',
          top: '64px',
          left: 0,
          width: `${SIDEBAR_WIDTH}px`,
          height: 'calc(100vh - 64px)',
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e5e7eb',
          zIndex: 900,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '8px',
          paddingBottom: '24px',
        }}
      >
        {visibleItems.map((item, i) => {
          if (item.type === 'divider') {
            return (
              <div
                key={`divider-${i}`}
                style={{
                  height: '1px',
                  backgroundColor: '#f3f4f6',
                  margin: '8px 16px',
                }}
              />
            );
          }

          // Collapsible parent (sub-menu) — e.g. "Active Modules".
          if (item.children) {
            const childActive = item.children.some((c) => isActive(c));
            const expanded = openMenus[item.label] !== undefined ? openMenus[item.label] : childActive;
            return (
              <React.Fragment key={`${item.label}-${i}`}>
                <button
                  onClick={() => setOpenMenus((m) => ({ ...m, [item.label]: !expanded }))}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '9px 16px', background: 'none', border: 'none',
                    borderLeft: childActive ? '3px solid #3a6ad9' : '3px solid transparent',
                    cursor: 'pointer', fontSize: '13px', fontWeight: childActive ? '600' : '400',
                    color: childActive ? '#3a6ad9' : '#374151', textAlign: 'left',
                    borderRadius: '0 6px 6px 0', marginRight: '8px',
                  }}>
                  <span>{item.label}</span>
                  <span style={{ fontSize: '9px', color: '#9ca3af', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s' }}>▶</span>
                </button>
                {expanded && item.children.map((child, ci) => {
                  const cActive = isActive(child);
                  return (
                    <button key={`${child.label}-${ci}`} onClick={() => navigate(child.path)}
                      style={{
                        display: 'flex', alignItems: 'center', width: '100%',
                        padding: '8px 16px 8px 32px', background: cActive ? '#eff4ff' : 'none',
                        border: 'none', borderLeft: cActive ? '3px solid #3a6ad9' : '3px solid transparent',
                        cursor: 'pointer', fontSize: '12.5px', fontWeight: cActive ? '600' : '400',
                        color: cActive ? '#3a6ad9' : '#6b7280', textAlign: 'left',
                        borderRadius: '0 6px 6px 0', marginRight: '8px',
                      }}
                      onMouseEnter={(e) => { if (!cActive) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={(e) => { if (!cActive) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                      <span>{child.label}</span>
                    </button>
                  );
                })}
              </React.Fragment>
            );
          }

          const active = isActive(item);
          const isPlaceholder = item.path === COMING_SOON;
          const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;

          return (
            <button
              key={`${item.label}-${i}`}
              onClick={() => navigate(item.path)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '9px 16px',
                background: active ? '#eff4ff' : 'none',
                border: 'none',
                borderLeft: active ? '3px solid #3a6ad9' : '3px solid transparent',
                cursor: isPlaceholder ? 'default' : 'pointer',
                fontSize: '13px',
                fontWeight: active ? '600' : '400',
                color: active ? '#3a6ad9' : isPlaceholder ? '#b0b7c3' : '#374151',
                textAlign: 'left',
                transition: 'background 0.12s',
                borderRadius: '0 6px 6px 0',
                marginRight: '8px',
              }}
              onMouseEnter={(e) => {
                if (!active && !isPlaceholder) e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span>{item.label}</span>
              {badgeCount > 0 && (
                <span style={{
                  backgroundColor: '#ff082f',
                  color: '#fff',
                  borderRadius: '9999px',
                  fontSize: '10px',
                  fontWeight: '700',
                  minWidth: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 4px',
                  flexShrink: 0,
                }}>
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </button>
          );
        })}

        {/* ── Profile + Legal footer ── */}
        <div style={{
          padding: '14px 16px 16px',
          borderTop: '1px solid #f3f4f6',
          marginTop: 'auto',
        }}>
          {/* Avatar + name row. Click opens a dropdown with Edit Profile
              / Settings / Log Out — Edit Profile + Log Out fire window
              CustomEvents that AdminNavbar listens for to surface the
              existing modals. Settings navigates directly. */}
          <div ref={accountRef} style={{ position: 'relative', marginBottom: 14 }}>
            <button
              type="button"
              onClick={() => setShowAccountDropdown((v) => !v)}
              aria-label="Open profile menu"
              aria-expanded={showAccountDropdown}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '6px 8px',
                background: showAccountDropdown ? '#f3f4f6' : 'transparent',
                border: '1px solid transparent', borderRadius: 8,
                cursor: 'pointer', textAlign: 'left',
                transition: 'background-color 0.15s, border-color 0.15s',
              }}
              onMouseEnter={(e) => { if (!showAccountDropdown) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
              onMouseLeave={(e) => { if (!showAccountDropdown) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '9999px',
                backgroundColor: '#3a6ad9', overflow: 'hidden', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <img
                  src={profilePhotoUrl || '/assets/doorly_icon_default.png'}
                  alt="Profile"
                  style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '9999px' }}
                  onError={(e) => { e.target.src = '/assets/doorly_icon_default.png'; }}
                />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {adminUser.firstName || adminUser.first_name || 'Admin'} {adminUser.lastName || adminUser.last_name || ''}
                </div>
                <div style={{ fontSize: 10, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {adminUser.email || ''}
                </div>
              </div>
            </button>

            {showAccountDropdown && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0,
                backgroundColor: '#fff', borderRadius: 10,
                border: '1px solid #e5e7eb', boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
                overflow: 'hidden', zIndex: 50,
              }}>
                {[
                  { label: 'Edit Profile', action: () => { fireEvent('doorly:openEditProfile'); setShowAccountDropdown(false); } },
                  // Staff user management — admin-only (backend gates POST /staff/invite on
                  // requireSuperAdmin). Hide the entry for non-admin roles so they don't
                  // click in and hit a 403.
                  ...(role === 'admin'
                    ? [{ label: 'Settings', action: () => { navigate('/admin/settings/users'); setShowAccountDropdown(false); } }]
                    : []),
                  { label: 'Log Out',      action: () => { fireEvent('doorly:openLogoutConfirm'); setShowAccountDropdown(false); }, danger: true },
                ].map((item) => (
                  <button key={item.label} type="button" onClick={item.action} style={{
                    width: '100%', textAlign: 'left', padding: '10px 14px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 600,
                    color: item.danger ? '#ff082f' : '#374151',
                  }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{
            fontSize: 11,
            fontWeight: 800,
            color: '#3a6ad9',
            letterSpacing: '0.04em',
            marginBottom: 8,
          }}>
            Doorly Admin Portal
          </div>
          <div style={{
            fontSize: 10,
            color: '#9ca3af',
            lineHeight: 1.7,
          }}>
            <div>Go Doorly, LLC</div>
            <div>NMLS #2764749</div>
            <div>Equal Housing Opportunity</div>
          </div>
        </div>
      </div>

      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .admin-sidebar { display: none !important; }
          .admin-sidebar-offset { padding-left: 0 !important; }
        }
      `}</style>
    </>
  );
}
